import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { EmailFindInterface } from '@zaoblako/shared/modules/integration/dtos/email/email-find.dto';
import type { MainSmsAccountFindInterface } from '@zaoblako/shared/modules/integration/dtos/main-sms/main-sms-account-find.dto';
import type { MailingFindInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-find.dto';
import type { MailingFormInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-form.dto';
import type { MailingTemplateFindInterface } from '@zaoblako/shared/modules/notification/dtos/mailing/mailing-template-find.dto';
import { MailingTypeEnum } from '@zaoblako/shared/modules/notification/enums/mailing.type.enum';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { IntegrationEmailService } from '@zaoblako/web/core/services/integration/integration-email.service';
import { IntegrationMainsmsService } from '@zaoblako/web/core/services/integration/integration-mainsms.service';
import { MailingHelpdeskService } from '@zaoblako/web/core/services/notification/mailing-helpdesk.service';
import { MailingTemplateService } from '@zaoblako/web/core/services/report/mailing-template.service';
import { MailingService } from '@zaoblako/web/core/services/report/mailing.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { NewsletterFormComponent } from '@zaoblako/web/modules/debts/dumbs/newsletter-form/newsletter-form.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import * as moment from 'moment-timezone';
import { forkJoin, Observable, tap } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-newsletter-form-sidebar',
  templateUrl: './newsletter-form-sidebar.component.html',
  imports: [CommonModule, NewsletterFormComponent, SpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsletterFormSidebarComponent implements OnInit {
  @Input() public readonly index: number;

  public data = <MailingFormInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public companies: CompanyFindInterface[] = [];
  public contragents: ContragentFindInterface[] = [];
  public emails: EmailFindInterface[] = [];
  public arraySms: MainSmsAccountFindInterface[] = [];
  public userGroups: UserGroupFindInterface[] = [];
  public externalUsers: ExternalUserFindInterface[] = [];
  public recipientUsers: UserFindInterface[] = [];
  public directions: DirectionFindInterface[] = [];

  public templateId: number;
  public contragentIds: number[];

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly mailingService: MailingService,
    private readonly mailingTemplateService: MailingTemplateService,
    private readonly integrationMainsmsService: IntegrationMainsmsService,
    private readonly integrationEmailService: IntegrationEmailService,
    private readonly mailingHelpdeskService: MailingHelpdeskService,
    private readonly companyService: CompanyService,
    private readonly contragentService: ContragentService,
    private readonly directionService: DirectionService,
    private readonly userGroupService: UserGroupService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {}

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.templateId = this.formStoreService.getState.forms[this.index]?.data?.templateId;
    let contragentIds: string | string[] = this.formStoreService.getState.forms[this.index]?.data?.contragentIds;
    if (contragentIds) {
      contragentIds = Array.isArray(contragentIds) ? contragentIds : [contragentIds];
      this.contragentIds = contragentIds.map((id) => parseInt(id));
    }

    if (this.templateId || this.contragentIds) {
      this.formStoreService.setLoading(true, this.index);
    }

    this.loadEntities().subscribe({
      next: () => {
        if (!isNaN(this.templateId) || this.contragentIds) {
          this.loadTemplate();
        }

        if (this.formStoreService.getState.forms[this.index]?.isEdit) {
          this.getById();
        } else {
          this.data.includingAdvancePayment = false;
          this.data.date = moment().startOf('minute').toDate();
        }
      }
    });
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.mailingService
      .findById<MailingFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (data) => {
          this.data = data;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  private loadTemplate() {
    this.mailingTemplateService.findById<MailingTemplateFindInterface>(this.templateId).subscribe((template) => {
      this.data = {
        channel: template.channel,
        fileDebtorTargets: template.fileDebtorTargets || [],
        companies: template.companies || [],
        contragents: this.contragentIds
          ? this.contragents.filter((contragent) => this.contragentIds.includes(contragent.id))
          : template.contragents || [],
        name: template.name,
        date: moment().startOf('minute').toDate(),
        text: template.text,
        title: template.title,
        type: this.contragentIds ? MailingTypeEnum.DEBTORS : template.type,
        includingAdvancePayment: template.includingAdvancePayment,
        externalUsers: template.externalUsers,
        recipientUsers: template.recipientUsers,
        userAuditors: template.userAuditors,
        groupAuditors: template.groupAuditors,
        attachmentStatuses: template.attachmentStatuses,
        emailSender: template.emailSender,
        smsSender: template.smsSender
      };
      this.formStoreService.setLoading(false, this.index);
      this.cdRef.markForCheck();
    });
  }

  public saveNewsletter(data: MailingFormInterface) {
    if (this.formStoreService.getState.forms[this.index]?.entityId) {
      this.formStoreService.setLoading(true, this.index);

      this.mailingService.update<MailingFormInterface, MailingFindInterface>(data).subscribe({
        next: (data) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage(`Рассылка "${data?.name}" обновлена!`);
          this.formStoreService.saveNext(data, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else {
      this.formStoreService.setLoading(true, this.index);

      this.mailingService.create<MailingFindInterface>(data).subscribe({
        next: (data) => {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addSuccessMessage('Рассылка создана!');
          this.formStoreService.saveNext(data, this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    }
  }

  public deleteNewsletter() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить рассылку "${this.data.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.mailingService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(`Рассылка "${this.data.name}" удалена!`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  private loadEntities(): Observable<any> {
    return forkJoin([
      this.mailingHelpdeskService.getHelpdeskContacts(),
      this.mailingHelpdeskService.getHelpdeskUsers(),
      this.integrationMainsmsService.find<MainSmsAccountFindInterface>(),
      this.integrationEmailService.find<EmailFindInterface>(),
      this.companyService.find<CompanyFindInterface>(),
      this.contragentService.search(),
      this.userGroupService.find<UserGroupFindInterface>(),
      this.directionService.find<DirectionFindInterface>()
    ]).pipe(
      tap(([contacts, recipientUsers, arraySms, emails, companies, contragents, userGroups, directions]) => {
        this.externalUsers = contacts;
        this.recipientUsers = recipientUsers;
        this.arraySms = arraySms;
        this.emails = emails;
        this.companies = companies;
        this.contragents = contragents;
        this.userGroups = userGroups;
        this.directions = directions;
        this.cdRef.markForCheck();
      })
    );
  }
}
